import { useState } from 'react';
import { Link } from 'react-router-dom';

import type {
    AdminCreditSowRegisterRow,
    AdminSowUsageSnapshot,
    AdminUserRow,
} from '../../services/api';
import { getAdminUserAuth0SyncState } from '../../lib/admin-user-auth0-syncability';
import { formatAdminUserLastLogin } from '../../lib/admin-user-last-login';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { ConfirmDialog } from '../ui/confirm-dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    getClickableRowProps,
} from '../ui/table';
import { StatusIcon } from '../ui/status-icon';
import { textLinkClassName } from '../ui/link-styles';
import { AgreementStatusBadge } from './credit/AgreementStatusBadge';
import {
    ADMIN_BADGE_BASE_CLASS,
    ADMIN_BADGE_POSITIVE_CLASS,
    ADMIN_BADGE_WARNING_CLASS,
} from './credit/adminStatusBadge';
import { formatCredits } from './credit/adminCreditAdapters';
import { cn } from '../../lib/utils';
import { SowTableUsageMetrics } from './credit/SowUsageDisplay.tsx';
import { AdminDashboardEmptyState } from './AdminDashboardPrimitives';
import type { CompanyDashboardRow } from './adminDashboardTypes';

function getUserAccessibleName(user: Pick<AdminUserRow, 'name' | 'email'>): string {
    return user.name || user.email;
}

function getSyncButtonAccessibleName(
    user: Pick<AdminUserRow, 'name' | 'email'>,
    isSyncing: boolean,
): string {
    return (isSyncing ? 'Syncing…' : 'Sync') + ' ' + getUserAccessibleName(user);
}

function formatUserCompanyLabels(user: AdminUserRow): string {
    if (user.memberships.length === 0) {
        return user.companyNames.join(', ') || '-';
    }
    const labels = new Map<number, string>();
    for (const membership of user.memberships) {
        const name = membership.companyName.trim() || 'Unnamed company';
        labels.set(membership.companyId, `${name} (#${membership.companyId})`);
    }
    return [...labels.values()].join(', ') || '-';
}

function formatUserTeamLabels(user: AdminUserRow): string {
    if (user.memberships.length === 0) {
        return user.teamNames.join(', ') || '-';
    }
    return (
        user.memberships
            .map((membership) => {
                const name = membership.teamName.trim() || 'Unnamed team';
                return `${name} (#${membership.teamId})`;
            })
            .join(', ') || '-'
    );
}

function isCompanySyncedToAuth0(company: CompanyDashboardRow): boolean {
    return Boolean(company.organizationId?.trim());
}

function Auth0SyncStatusBadge({ synced }: { synced: boolean }) {
    return (
        <span
            className={cn(
                ADMIN_BADGE_BASE_CLASS,
                synced ? ADMIN_BADGE_POSITIVE_CLASS : ADMIN_BADGE_WARNING_CLASS,
            )}
        >
            {synced ? 'Synced' : 'Not synced'}
        </span>
    );
}

export function AdminCompaniesTable({
    rows,
    onRowClick,
}: {
    rows: CompanyDashboardRow[];
    onRowClick: (company: CompanyDashboardRow) => void;
}) {
    if (rows.length === 0) {
        return <AdminDashboardEmptyState message="No companies found." />;
    }

    return (
        <Card className="overflow-clip">
            <Table stickyHeader aria-label="Companies" className="min-w-[560px] table-fixed">
                <TableHeader>
                    <TableRow>
                        <TableHead className="min-w-0">Company</TableHead>
                        <TableHead
                            className="w-14 shrink-0 px-2 text-center sm:px-3"
                            title="Company account is active"
                        >
                            Active
                        </TableHead>
                        <TableHead
                            className="w-28 shrink-0 px-2 text-center sm:px-3"
                            title="Company has an Auth0 organization ID"
                        >
                            Auth0
                        </TableHead>
                        <TableHead
                            className="w-24 shrink-0 whitespace-nowrap px-2 text-center sm:px-3"
                            title="Has active SOW"
                        >
                            SOW
                        </TableHead>
                        <TableHead className="w-[8.5rem] shrink-0 text-right">Usage</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((company) => (
                        <TableRow
                            key={company.companyId}
                            className="cursor-pointer"
                            {...getClickableRowProps(() => onRowClick(company))}
                        >
                            <TableCell className="min-w-0">
                                <p className="break-words font-medium text-neutral-950">
                                    {company.name}
                                </p>
                                <p className="text-xs text-neutral-500">ID: {company.companyId}</p>
                            </TableCell>
                            <TableCell className="w-14 shrink-0 px-2 text-center sm:px-3">
                                <StatusIcon value={company.isActive} />
                            </TableCell>
                            <TableCell className="w-28 shrink-0 px-2 text-center sm:px-3">
                                <Auth0SyncStatusBadge synced={isCompanySyncedToAuth0(company)} />
                            </TableCell>
                            <TableCell className="w-24 shrink-0 px-2 text-center sm:px-3">
                                <StatusIcon value={company.activeSowCount > 0} />
                            </TableCell>
                            <TableCell className="w-[8.5rem] shrink-0 text-right">
                                {company.activeSowCount === 0 ? (
                                    <span className="text-neutral-500">-</span>
                                ) : company.activeSowCount > 1 ? (
                                    <span className="text-sm text-neutral-500">
                                        Multiple active SOWs
                                    </span>
                                ) : (
                                    <>
                                        <p className="font-medium">{formatCredits(company.used)}</p>
                                        <p className="text-xs text-neutral-500">
                                            {company.usagePercent}% used
                                        </p>
                                    </>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Card>
    );
}

export function AdminSowsTable({
    rows,
    onRowClick,
}: {
    rows: AdminCreditSowRegisterRow[];
    onRowClick: (sowId: string) => void;
}) {
    const companyLinkClass = textLinkClassName;

    if (rows.length === 0) {
        return <AdminDashboardEmptyState message="No SOWs found." />;
    }

    return (
        <Card className="overflow-clip">
            <Table stickyHeader aria-label="SOWs" className="min-w-[48rem]">
                <TableHeader>
                    <TableRow>
                        <TableHead>SOW</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Period</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Pro usage</TableHead>
                        <TableHead className="text-right">Pulse usage</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((sow) => (
                        <TableRow
                            key={sow.id}
                            className="cursor-pointer"
                            {...getClickableRowProps(() => onRowClick(sow.id))}
                        >
                            <TableCell>
                                <p className="font-medium text-neutral-950">{sow.name}</p>
                                <p className="text-xs text-neutral-500">{sow.id}</p>
                            </TableCell>
                            <TableCell className="max-w-[min(20rem,40vw)]">
                                <span className="flex flex-wrap items-baseline gap-x-1 gap-y-0.5 text-sm">
                                    <Link
                                        to={`/admin/companies/${sow.companyId}`}
                                        state={{
                                            adminCompanyDetail: {
                                                companyId: String(sow.companyId),
                                                companyLabel: sow.companyName,
                                            },
                                        }}
                                        className={companyLinkClass}
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        {sow.companyName.trim() || 'Unnamed company'}
                                    </Link>
                                    <span className="tabular-nums text-neutral-500">
                                        (#{sow.companyId})
                                    </span>
                                </span>
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                                {new Date(sow.startDate).toLocaleDateString()} -{' '}
                                {sow.endDate ? new Date(sow.endDate).toLocaleDateString() : 'Open'}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                                <AgreementStatusBadge status={sow.state} />
                            </TableCell>
                            <TableCell className="text-right">
                                <SowPoolUsageCell usage={sow.proUsage} />
                            </TableCell>
                            <TableCell className="text-right">
                                <SowPoolUsageCell usage={sow.pulseUsage} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Card>
    );
}

function SowPoolUsageCell({ usage }: { usage: AdminSowUsageSnapshot | null }) {
    if (!usage) {
        return <span className="text-neutral-500">-</span>;
    }
    return (
        <SowTableUsageMetrics
            used={usage.used}
            granted={usage.granted}
            usageCommitted={usage.usageCommitted}
            usagePending={usage.usagePending}
            usagePercent={usage.usagePercent}
            committedUsagePercent={usage.committedUsagePercent}
            pendingUsagePercent={usage.pendingUsagePercent}
        />
    );
}

export function AdminUsersTable({
    onRowClick,
    onSync,
    rows,
    syncingUserId,
}: {
    rows: AdminUserRow[];
    syncingUserId: number | null;
    onRowClick: (user: AdminUserRow) => void;
    onSync: (userId: number) => void;
}) {
    const [syncConfirmUser, setSyncConfirmUser] = useState<AdminUserRow | null>(null);

    if (rows.length === 0) {
        return <AdminDashboardEmptyState message="No users found." />;
    }

    const linkClass = textLinkClassName;
    const syncConfirmLabel = syncConfirmUser ? getUserAccessibleName(syncConfirmUser) : '';

    return (
        <>
            <Card className="overflow-clip">
                <Table stickyHeader aria-label="Users" className="min-w-[56rem] table-fixed">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[15%]">Name</TableHead>
                            <TableHead className="w-[22%]">Email</TableHead>
                            <TableHead className="w-[22%]">Companies / teams</TableHead>
                            <TableHead className="w-14 text-center">SSO</TableHead>
                            <TableHead className="w-16 text-center">Active</TableHead>
                            <TableHead className="w-40">Last login</TableHead>
                            <TableHead className="w-28">Auth0</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((user) => {
                            const syncState = getAdminUserAuth0SyncState(user);
                            const isSyncing = syncingUserId === user.id;

                            return (
                                <TableRow
                                    key={user.id}
                                    className="cursor-pointer [&>td]:align-top"
                                    {...getClickableRowProps(() => onRowClick(user))}
                                >
                                    <TableCell className="break-words font-medium text-neutral-950">
                                        {user.name}
                                        <p className="mt-0.5 text-xs font-normal text-neutral-500">
                                            ID: {user.id}
                                        </p>
                                    </TableCell>
                                    <TableCell className="break-words text-neutral-800">
                                        {user.email}
                                    </TableCell>
                                    <TableCell className="text-neutral-800">
                                        {user.memberships.length === 0 ? (
                                            <div className="space-y-1 break-words text-sm">
                                                <p>
                                                    <span className="text-neutral-500">
                                                        Companies:{' '}
                                                    </span>
                                                    {formatUserCompanyLabels(user)}
                                                </p>
                                                <p>
                                                    <span className="text-neutral-500">
                                                        Teams:{' '}
                                                    </span>
                                                    {formatUserTeamLabels(user)}
                                                </p>
                                            </div>
                                        ) : (
                                            <ul className="list-none space-y-2 break-words">
                                                {user.memberships.map((membership) => (
                                                    <li
                                                        key={`${membership.companyId}-${membership.teamId}`}
                                                    >
                                                        <span className="flex flex-wrap items-baseline gap-x-1 gap-y-0.5 text-sm">
                                                            <Link
                                                                to={`/admin/companies/${membership.companyId}`}
                                                                state={{
                                                                    adminCompanyDetail: {
                                                                        companyId: String(
                                                                            membership.companyId,
                                                                        ),
                                                                        companyLabel:
                                                                            membership.companyName,
                                                                    },
                                                                }}
                                                                className={linkClass}
                                                                onClick={(event) =>
                                                                    event.stopPropagation()
                                                                }
                                                            >
                                                                {membership.companyName.trim() ||
                                                                    'Unnamed company'}
                                                            </Link>
                                                            <span className="tabular-nums text-neutral-500">
                                                                (#{membership.companyId})
                                                            </span>
                                                            <span className="text-neutral-400">
                                                                /
                                                            </span>
                                                            <Link
                                                                to={`/admin/companies/${membership.companyId}?tab=teams&expandTeam=${encodeURIComponent(String(membership.teamId))}`}
                                                                state={{
                                                                    adminCompanyDetail: {
                                                                        companyId: String(
                                                                            membership.companyId,
                                                                        ),
                                                                        companyLabel:
                                                                            membership.companyName,
                                                                    },
                                                                }}
                                                                className={linkClass}
                                                                onClick={(event) =>
                                                                    event.stopPropagation()
                                                                }
                                                            >
                                                                {membership.teamName.trim() ||
                                                                    'Unnamed team'}
                                                            </Link>
                                                            <span className="tabular-nums text-neutral-500">
                                                                (#{membership.teamId})
                                                            </span>
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-center text-neutral-800">
                                        <StatusIcon
                                            value={user.isSso}
                                            falseTone="neutral"
                                            label={user.isSso ? 'SSO enabled' : 'SSO disabled'}
                                        />
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <StatusIcon value={user.isActive} />
                                    </TableCell>
                                    <TableCell className="text-sm text-neutral-700">
                                        {formatAdminUserLastLogin(user)}
                                    </TableCell>
                                    <TableCell>
                                        {user.isSyncedToAuth0 ? (
                                            <StatusIcon value />
                                        ) : (
                                            <span className="inline-flex items-center gap-2">
                                                <StatusIcon value={false} />
                                                <Button
                                                    type="button"
                                                    variant="destructiveOutline"
                                                    size="sm"
                                                    loading={isSyncing}
                                                    disabled={isSyncing || !syncState.canSync}
                                                    title={
                                                        !syncState.canSync
                                                            ? (syncState.disabledReason ??
                                                              undefined)
                                                            : undefined
                                                    }
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        setSyncConfirmUser(user);
                                                    }}
                                                    aria-label={getSyncButtonAccessibleName(
                                                        user,
                                                        isSyncing,
                                                    )}
                                                >
                                                    {isSyncing ? 'Syncing…' : 'Sync'}
                                                </Button>
                                            </span>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Card>
            <ConfirmDialog
                open={syncConfirmUser !== null}
                destructive
                title={`Sync ${syncConfirmLabel} to Auth0?`}
                description={`${syncConfirmLabel} will be synced to Auth0.`}
                confirmLabel="Sync user"
                pendingLabel="Syncing…"
                onCancel={() => setSyncConfirmUser(null)}
                onConfirm={() => {
                    const user = syncConfirmUser;
                    if (!user) return;
                    setSyncConfirmUser(null);
                    onSync(user.id);
                }}
            />
        </>
    );
}
